import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useStepProgress } from '../../../hooks/useStepProgress';
import { useStepChecklist } from '../../../hooks/useStepChecklist';
import { useAuth } from '../../../hooks/useAuth';

const stepNames = [
  'テーマ設定',
  '先行研究レビュー',
  'リサーチクエスチョン策定',
  '研究設計',
  'データ収集',
  'データ分析',
  '考察・執筆',
  '発表準備',
];

interface RoadmapProgressWidgetProps {
  layoutMode?: 'normal' | 'compact';
}

function useWidgetInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin: '0px 0px -20px 0px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}

/**
 * RoadmapProgressWidget - タイムライン形式で研究ロードマップの進捗を表示
 */
export default function RoadmapProgressWidget({ layoutMode = 'normal' }: RoadmapProgressWidgetProps) {
  const { user } = useAuth();
  const { progressData, getCurrentStepIndex, getOverallProgress, getStepProgress } = useStepProgress();
  const { getStepCompletionRate } = useStepChecklist();
  const { ref: widgetRef, isVisible } = useWidgetInView(0.15);

  // -------------------------------------------------------------------------
  // When the user is not logged in – show a minimal CTA widget
  // -------------------------------------------------------------------------
  if (!user) {
    return (
      <div
        ref={widgetRef}
        className={`bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border border-slate-200 dark:border-gray-700 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        }`}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg flex items-center justify-center text-white">
            <i className="ri-map-pin-line text-xl" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">研究ガイド</h3>
        </div>

        <p className="text-slate-600 dark:text-gray-400 text-sm mb-4">
          ログインして研究の進捗を管理しましょう
        </p>

        <Link
          to="/portfolio"
          className="block w-full px-4 py-2 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors text-center cursor-pointer"
        >
          研究経過報告を見る
        </Link>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Gather progress statistics
  // -------------------------------------------------------------------------
  let currentStepIndex = 0;
  let overallProgress = 0;

  try {
    currentStepIndex = getCurrentStepIndex();
    overallProgress = getOverallProgress();
  } catch (e) {
    console.error('Failed to obtain roadmap progress data:', e);
  }

  const completedSteps = progressData.filter(p => p.status === 'completed').length;
  const inProgressSteps = progressData.filter(p => p.status === 'in_progress').length;

  // チェックリストの総数（各ステップ7項目）
  const checklistItemsPerStep = 7;

  // -------------------------------------------------------------------------
  // タイムライン形式のメインUI
  // -------------------------------------------------------------------------
  return (
    <div
      ref={widgetRef}
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-md border border-slate-200 dark:border-gray-700 overflow-hidden transition-all duration-700 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      {/* Header */}
      <div className="relative p-6 pb-4 bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20">
        <div className="flex items-center gap-3 mb-4">
          <div
            className={`w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg flex items-center justify-center text-white shadow-md shadow-teal-200/40 transition-all duration-700 ${
              isVisible ? 'scale-100 rotate-0' : 'scale-75 rotate-12'
            }`}
          >
            <i className="ri-road-map-line text-xl" />
          </div>

          <div className="flex-1">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">研究ロードマップ</h3>
            <p className="text-sm text-slate-600 dark:text-gray-400">タイムライン進捗</p>
          </div>
        </div>

        {/* Overall progress summary */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-teal-100 dark:border-teal-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-700 dark:text-gray-300">全体進捗</span>
            <span
              className={`text-lg font-bold text-teal-600 dark:text-teal-400 transition-all duration-700 ${
                isVisible ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {overallProgress}%
            </span>
          </div>

          <div className="w-full bg-slate-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden mb-3">
            <div
              className="h-full bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full transition-all ease-out relative"
              style={{
                width: isVisible ? `${overallProgress}%` : '0%',
                transitionDuration: '1.2s',
                transitionDelay: '0.3s',
              }}
            >
              <div className="absolute inset-0 shimmer-line rounded-full" />
            </div>
          </div>

          <div className="flex items-center justify-between text-xs">
            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">✓ {completedSteps}完了</span>
            <span className="text-teal-600 dark:text-teal-400 font-semibold">→ {inProgressSteps}進行中</span>
            <span className="text-slate-500 dark:text-gray-400">{stepNames.length - completedSteps - inProgressSteps}未着手</span>
          </div>
        </div>
      </div>

      {/* タイムライン */}
      <div className="px-6 pb-6 pt-2">
        <div className="relative">
          {/* タイムラインの縦線 */}
          <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-teal-300 via-emerald-200 to-teal-300 dark:from-teal-700 dark:via-emerald-800 dark:to-teal-700"></div>

          {/* ステップリスト */}
          <div className="space-y-3">
            {stepNames.map((name, index) => {
              const stepId = index + 1;
              const progress = getStepProgress(stepId);
              const status = progress?.status || 'not_started';
              const completionRate = getStepCompletionRate(stepId, checklistItemsPerStep);
              const completedAt = progress?.completed_at;
              const remainingTasks = checklistItemsPerStep - Math.round((completionRate / 100) * checklistItemsPerStep);

              return (
                <div
                  key={index}
                  className={`relative pl-10 transition-all duration-500 ${
                    isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-3'
                  }`}
                  style={{ transitionDelay: `${0.4 + index * 0.06}s` }}
                >
                  {/* タイムラインノード */}
                  <div
                    className={`absolute left-0 top-1 w-[31px] h-[31px] rounded-full flex items-center justify-center border-2 transition-all duration-300 z-10 ${
                      status === 'completed'
                        ? 'bg-emerald-500 border-emerald-300 dark:border-emerald-700 text-white shadow-md shadow-emerald-200/50 dark:shadow-emerald-900/30'
                        : status === 'in_progress'
                        ? 'bg-white dark:bg-gray-800 border-teal-400 dark:border-teal-600 text-teal-600 dark:text-teal-400 shadow-md shadow-teal-100/50 dark:shadow-teal-900/30 animate-timeline-pulse'
                        : 'bg-white dark:bg-gray-800 border-slate-300 dark:border-gray-600 text-slate-400 dark:text-gray-500'
                    }`}
                  >
                    {status === 'completed' ? (
                      <i className="ri-check-line text-sm" />
                    ) : status === 'in_progress' ? (
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500"></span>
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold">{stepId}</span>
                    )}
                  </div>

                  {/* ステップカード */}
                  <div
                    className={`bg-slate-50 dark:bg-gray-700/50 rounded-lg p-3 border transition-all duration-300 hover:shadow-md ${
                      status === 'completed'
                        ? 'border-emerald-200 dark:border-emerald-800/50'
                        : status === 'in_progress'
                        ? 'border-teal-200 dark:border-teal-800/50 bg-teal-50/50 dark:bg-teal-900/10'
                        : 'border-slate-200 dark:border-gray-600'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-[10px] font-semibold text-slate-400 dark:text-gray-500 tracking-wider">STEP {stepId}</span>
                          {status === 'completed' && (
                            <span className="px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-[9px] font-semibold rounded-full">完了</span>
                          )}
                          {status === 'in_progress' && (
                            <span className="px-1.5 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 text-[9px] font-semibold rounded-full">進行中</span>
                          )}
                        </div>
                        <h4
                          className={`text-sm font-semibold transition-colors duration-300 ${
                            status === 'completed'
                              ? 'text-slate-400 dark:text-gray-500 line-through'
                              : status === 'in_progress'
                              ? 'text-teal-700 dark:text-teal-400'
                              : 'text-slate-700 dark:text-gray-300'
                          }`}
                        >
                          {name}
                        </h4>
                      </div>
                    </div>

                    {/* 完了率プログレスバー（進行中・完了時のみ） */}
                    {(status === 'in_progress' || status === 'completed') && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] text-slate-500 dark:text-gray-400">チェックリスト進捗</span>
                          <span className="text-[10px] font-semibold text-teal-600 dark:text-teal-400">{completionRate}%</span>
                        </div>
                        <div className="w-full bg-slate-200 dark:bg-gray-600 rounded-full h-1.5 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${
                              status === 'completed'
                                ? 'bg-gradient-to-r from-emerald-500 to-emerald-400'
                                : 'bg-gradient-to-r from-teal-500 to-teal-400'
                            }`}
                            style={{ width: `${completionRate}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {/* 完了日時 */}
                    {status === 'completed' && completedAt && (
                      <div className="mt-2 flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400">
                        <i className="ri-calendar-check-line"></i>
                        <span>{new Date(completedAt).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' })}完了</span>
                      </div>
                    )}

                    {/* 残りタスク数（進行中のみ） */}
                    {status === 'in_progress' && remainingTasks > 0 && (
                      <div className="mt-2 flex items-center gap-1 text-[10px] text-teal-600 dark:text-teal-400">
                        <i className="ri-task-line"></i>
                        <span>残り{remainingTasks}タスク</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* CTA link */}
      <div className="px-6 pb-6 pt-0">
        <Link
          to="/portfolio#roadmap"
          className={`block w-full px-4 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-lg font-medium hover:from-teal-700 hover:to-emerald-700 transition-all text-center cursor-pointer shadow-md shadow-teal-200/30 dark:shadow-teal-900/20 hover:shadow-lg hover:shadow-teal-200/40 duration-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
          }`}
          style={{ transitionDelay: '1.1s' }}
        >
          詳細を見る
        </Link>
      </div>
    </div>
  );
}